import {
  Box,
  ButtonBase,
  Card,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@material-ui/core'
import { Confirm, Spacer, useToasts } from '@vestaboard/installables'
import React, { useState } from 'react'

import { Alert } from '@material-ui/lab'
import { Delete } from '@material-ui/icons'
import InfiniteScroll from 'react-infinite-scroller'
import { TruncatedId } from '../../truncated-id'
import { useDeleteMessageSetMessage } from '../hooks/useDeleteMessageSetMesage'
import { useListMessageMessageSets } from '../hooks/useListMessageMessageSets'

interface IListMessageSetsProps {
  messageId: string
}

export const ListMessageSets = (props: IListMessageSetsProps) => {
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const { data, loading, error, fetchMore, refetch } = useListMessageMessageSets(props.messageId)
  const [removing, setRemoving] = useState<string | null>(null)
  const [deleteMessageSetMessage] = useDeleteMessageSetMessage()
  const { addToast } = useToasts()

  const appearances = data?.listSearchMessagesV2?.searchMessages || []

  return (
    <>
      <Confirm
        title='Remove Message from Message Set?'
        open={!!removing}
        handleClose={() => setRemoving(null)}
        handleAccept={async () => {
          if (!removing) {
            return
          }

          try {
            await deleteMessageSetMessage({
              variables: {
                input: {
                  messageSetId: removing,
                  messageId: props.messageId
                }
              }
            })

            addToast('Message removed from message set', { appearance: 'success' })
            setRemoving(null)

            await refetch()
          } catch (err) {
            addToast('There was an error removing the message from the message set', { appearance: 'error' })
            setRemoving(null)
          }
        }}
      />
      {loading ? (
        <CircularProgress />
      ) : error ? (
        <Alert severity='error'>There was an error loading the message flags</Alert>
      ) : !appearances?.length ? (
        <Alert severity='info'>This message has not been flagged</Alert>
      ) : appearances.length ? (
        <Card>
          <InfiniteScroll
            pageStart={0}
            loadMore={async () => {
              if (isLoadingMore) {
                return
              }

              setIsLoadingMore(true)

              return await fetchMore({
                updateQuery(previousQueryResult: any, options: any) {
                  if (options.fetchMoreResult.message.__typename !== 'MessageV2') {
                    return options.fetchMoreResult
                  }

                  const newMessageSets = options.fetchMoreResult?.message?.listFlaggedMessages?.flaggedMessages || []
                  const previousMessageSets = previousQueryResult?.message?.listFlaggedMessages?.flaggedMessages || []

                  const result = {
                    ...previousQueryResult,
                    message: {
                      ...previousQueryResult.message,
                      listFlaggedMessages: {
                        ...previousQueryResult.message.listFlaggedMessages,
                        nextCursor: options.fetchMoreResult?.message.listFlaggedMessages.nextCursor || null,
                        flaggedMessages: [...previousMessageSets, ...newMessageSets]
                      }
                    }
                  }

                  return result
                },
                variables: {
                  id: props.messageId,
                  input: {
                    cursor: data?.listSearchMessagesV2?.nextCursor || null
                  }
                }
              }).then(res => {
                setIsLoadingMore(false)
                return res
              })
            }}
            hasMore={!!data?.listSearchMessagesV2?.nextCursor}
            loader={
              <Box>
                <Spacer size='large' />
                <CircularProgress />
              </Box>
            }>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>Title</TableCell>
                    <TableCell>Remove from Search</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {appearances.map(item =>
                    item.__typename === 'MessageSetMessageV2' ? (
                      <TableRow key={item.id}>
                        <TableCell>
                          <TruncatedId value={item.messageSet.id} full path={`/message-set/${item.messageSet.id}`} />
                        </TableCell>
                        <TableCell>{item.messageSet.title || ''}</TableCell>
                        <TableCell>
                          <ButtonBase
                            onClick={() => {
                              setRemoving(item.messageSet.id)
                            }}>
                            <Delete />
                          </ButtonBase>
                        </TableCell>
                      </TableRow>
                    ) : null
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </InfiniteScroll>
        </Card>
      ) : (
        <Alert severity='error'>Oops... something went wrong.</Alert>
      )}
    </>
  )
}
